/**
 * DAW Components Index
 * Modular components extracted from DAWWorkspace.jsx
 */

export { TrackControls } from './TrackControls';
export { TrackLane } from './TrackLane';
export { TimeRuler } from './TimeRuler';
export { EffectsPanel } from './EffectsPanel';
export { TransportBar } from './TransportBar';
export { Toolbar } from './Toolbar';
export { ToolbarActions } from './ToolbarActions';
export { MixerSidebar } from './MixerSidebar';
export { TrackSidebar } from './TrackSidebar';
export { TransportControls } from './TransportControls';
export { EditingTools } from './EditingTools';
export { TimelineHeader } from './TimelineHeader';
export { ExportButtons } from './ExportButtons';
export { ProjectControls } from './ProjectControls';
export { MIDIPanel, MIDIIndicator } from './MIDIPanel';
export { 
  DAWOverlays, 
  CountInOverlay, 
  ExportProgressOverlay, 
  LoadingOverlay, 
  SaveProgressOverlay 
} from './Overlays';
export { StatusBar } from './StatusBar';
export { TrackListPanel } from './TrackListPanel';
export { ArrangementView } from './ArrangementView';

// Inline components extracted from DAWWorkspace.jsx (fully compatible)
export { InlineTrackControls } from './InlineTrackControls';
export { InlineTrackLane } from './InlineTrackLane';
export { InlineTimeRuler } from './InlineTimeRuler';
export { InlineEffectsPanel } from './InlineEffectsPanel';
export { MIDIButtonWithPanel } from './MIDIButtonWithPanel';
export { MetronomeIcon } from './MetronomeIcon';
export { LoopToggle } from './LoopToggle';
export { GridSnapToggle } from './GridSnapToggle';
export { AutomationToggle } from './AutomationToggle';
export { InputMonitorToggle } from './InputMonitorToggle';
export { AudioInputButton } from './AudioInputButton';
export { InstrumentsToggle } from './InstrumentsToggle';
export { UndoRedoButtons } from './UndoRedoButtons';
export { SmartTempoToggle } from './SmartTempoToggle';
export { ChordTrackToggle } from './ChordTrackToggle';
export { ChordWizardButton } from './ChordWizardButton';
export { EffectsToggle } from './EffectsToggle';
export { MasteringToggle } from './MasteringToggle';
export { PluginRackToggle } from './PluginRackToggle';
export { QuickStartButton } from './QuickStartButton';
export { AIMixingToggle } from './AIMixingToggle';
export { AIMasteringToggle } from './AIMasteringToggle';
export { CollaborationButton } from './CollaborationButton';
export { JamSessionButton } from './JamSessionButton';
export { 
  AIMixAssistantButton, 
  AIChordGeneratorButton, 
  AILyricAssistantButton, 
  AIDrumGeneratorButton 
} from './AIToolButtons';
export { VideoSyncButton } from './VideoSyncButton';
export { SampleLibraryButton } from './SampleLibraryButton';
export { PluginBrowserButton, PluginMarketplaceButton } from './PluginButtons';
export { KeyboardShortcutsButton } from './KeyboardShortcutsButton';
export { ThemeToggleButton } from './ThemeToggleButton';
export { DuplicateProjectButton } from './DuplicateProjectButton';
export { UserMenu } from './UserMenu';
export { MobileMixerToggle } from './MobileMixerToggle';
export { LoopRegionOverlay } from './LoopRegionOverlay';
export { Playhead } from './Playhead';
export { BPMDisplay } from './BPMDisplay';
export { TimeDisplay } from './TimeDisplay';
export { PlaybackControls } from './PlaybackControls';
export { ProjectHeader } from './ProjectHeader';
export { MetronomeToggle } from './MetronomeToggle';
export { MainTransportButtons } from './MainTransportButtons';
export { AdvancedEffectsModal } from './AdvancedEffectsModal';
export { MarkersPanelWrapper } from './MarkersPanelWrapper';
export { MarkerPickerWrapper } from './MarkerPickerWrapper';
export { AIChordGeneratorWrapper } from './AIChordGeneratorWrapper';
export { AIDrumGeneratorWrapper } from './AIDrumGeneratorWrapper';
export { SampleLibraryWrapper } from './SampleLibraryWrapper';
export { AIMixingPanelWrapper } from './AIMixingPanelWrapper';
export { MasteringPanelWrapper } from './MasteringPanelWrapper';
export { AIMasteringPanelWrapper } from './AIMasteringPanelWrapper';
export { CollaborationPanelWrapper } from './CollaborationPanelWrapper';
export { SmartTempoPanelWrapper } from './SmartTempoPanelWrapper';
export { EffectsPanelWrapper } from './EffectsPanelWrapper';
export { JamSessionPanelWrapper } from './JamSessionPanelWrapper';
export { PluginRackPanel } from './PluginRackPanel';
export { PianoRollWrapper } from './PianoRollWrapper';
export { SessionManagerWrapper } from './SessionManagerWrapper';
export { JamSessionModalWrapper } from './JamSessionModalWrapper';
export { PluginMarketplaceWrapper } from './PluginMarketplaceWrapper';
export { BatchExporterWrapper } from './BatchExporterWrapper';
export { VideoSyncPanelWrapper } from './VideoSyncPanelWrapper';
export { ChordWizardWrapper } from './ChordWizardWrapper';
export { QuickStartWrapper } from './QuickStartWrapper';
export { AILyricAssistantWrapper } from './AILyricAssistantWrapper';
export { AIMixAssistantWrapper } from './AIMixAssistantWrapper';
export { EffectPresetsBrowserWrapper } from './EffectPresetsBrowserWrapper';
export { PluginBrowserWrapper } from './PluginBrowserWrapper';
export { ChainPresetSelectorWrapper } from './ChainPresetSelectorWrapper';
export { KeyboardShortcutsPanelWrapper } from './KeyboardShortcutsPanelWrapper';
export { WaveformToolbarWrapper } from './WaveformToolbarWrapper';
export { InstrumentsPanelWrapper } from './InstrumentsPanelWrapper';
export { RemoteCursorsWrapper } from './RemoteCursorsWrapper';
export { HeaderToolbar } from './HeaderToolbar';
export { TimelineContent } from './TimelineContent';
export { EffectsSidebarWrapper } from './EffectsSidebarWrapper';
export { MixerPanelWrapper } from './MixerPanelWrapper';
export { MixViewWrapper } from './MixViewWrapper';
export { TrackSidebarWrapper } from './TrackSidebarWrapper';
export { DAWModalsContainer } from './DAWModalsContainer';
export { QuickMixProPanel } from './QuickMixProPanel';

// Pure logic functions for testability
export {
  formatTime,
  calculateLoopStart,
  calculateLoopEnd,
  calculateLoopRegionMove,
  isWithinLoop,
  getLoopedTime,
  TransportState,
  getNextTransportState,
  pixelToTime,
  timeToPixel,
  validateReconnection,
  MIN_LOOP_DURATION,
} from './logic';

// Constants used across DAW components
export const MAX_TRACKS = 8;
export const SAMPLE_RATE = 44100;
export const DEFAULT_BPM = 120;
export const CHUNK_SIZE = 2 * 1024 * 1024; // 2MB chunks for upload

// Re-export extended constants
export * from './constants';

// Re-export hooks
export * from './hooks';

// Note: utils/audioUtils has its own exports to avoid circular deps
// Import directly from './utils/audioUtils' if needed

export const TRACK_COLORS = [
  'from-cyan-500 to-cyan-600',
  'from-purple-500 to-purple-600',
  'from-amber-500 to-amber-600',
  'from-emerald-500 to-emerald-600',
  'from-rose-500 to-rose-600',
  'from-indigo-500 to-indigo-600',
  'from-lime-500 to-lime-600',
  'from-fuchsia-500 to-fuchsia-600',
];

// Track color mapping for clips
export const TRACK_COLOR_MAP = {
  'from-cyan-500': 'rgba(6, 182, 212, 0.7)',
  'from-purple-500': 'rgba(168, 85, 247, 0.7)',
  'from-amber-500': 'rgba(245, 158, 11, 0.7)',
  'from-emerald-500': 'rgba(16, 185, 129, 0.7)',
  'from-rose-500': 'rgba(244, 63, 94, 0.7)',
  'from-indigo-500': 'rgba(99, 102, 241, 0.7)',
  'from-lime-500': 'rgba(132, 204, 22, 0.7)',
  'from-fuchsia-500': 'rgba(217, 70, 239, 0.7)',
};

// Track factory function
export const createEmptyTrack = (id, name) => ({
  id,
  name,
  type: 'audio',
  color: TRACK_COLORS[id % TRACK_COLORS.length],
  volume: 0.8,
  pan: 0,
  muted: false,
  solo: false,
  duration: 60,
  startTime: 0,
  originalDuration: 60,
  waveformData: [],
  audioBuffer: null,
  eq: {
    low: 0,
    mid: 0,
    high: 0,
  },
  effects: {
    reverb: 0,
    delay: 0,
    chorus: 0,
    compression: 0,
    distortion: 0,
  },
});

// Audio utility functions
export const audioBufferToBase64 = async (audioBuffer) => {
  const interleaved = new Float32Array(audioBuffer.length * audioBuffer.numberOfChannels);
  for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
    const channelData = audioBuffer.getChannelData(channel);
    for (let i = 0; i < audioBuffer.length; i++) {
      interleaved[i * audioBuffer.numberOfChannels + channel] = channelData[i];
    }
  }
  const uint8Array = new Uint8Array(interleaved.buffer);
  let binary = '';
  for (let i = 0; i < uint8Array.length; i++) {
    binary += String.fromCharCode(uint8Array[i]);
  }
  return btoa(binary);
};

export const base64ToAudioBuffer = async (base64, audioContext) => {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  const floats = new Float32Array(bytes.buffer);
  const buffer = audioContext.createBuffer(2, floats.length / 2, SAMPLE_RATE);
  for (let channel = 0; channel < 2; channel++) {
    const channelData = buffer.getChannelData(channel);
    for (let i = 0; i < buffer.length; i++) {
      channelData[i] = floats[i * 2 + channel];
    }
  }
  return buffer;
};

export const splitIntoChunks = (base64String, chunkSize = CHUNK_SIZE) => {
  const chunks = [];
  for (let i = 0; i < base64String.length; i += chunkSize) {
    chunks.push(base64String.slice(i, i + chunkSize));
  }
  return chunks;
};
